import axios from 'axios';
import { getScaleObject, scaleList, timePeriodList } from '../store/StaticVariables';
import { fieldMap } from '../utils/DataTransformation';
import { endpoints } from '../store/StaticVariables';
import { convertScaleValues } from '../store/StaticVariables';

const baseUrl = (time, scaleID) => endpoints.features + time.toUpperCase() + `/MapServer/${scaleID}/query`;

const addFieldIndexNames = (obj) => {
    return Object.keys(obj).map((key) => {
        var outFields = obj[key].split(',').map((i) => i.trim());
        var indexFields = outFields.map((field) => {
            if (!(field in fieldMap)) return field;
            return fieldMap[field].label;
        });
        return { slug: key, index: indexFields, outFields: outFields.join(',') };
    });
};

var fields = addFieldIndexNames(endpoints.downloadFields);

const filterFieldsByTime = (fieldStr, time) => {
    if (time + ''.toLowerCase() === 'ttm' || time + '' === '2023' || time + '' === '2022') return fieldStr;
    return fieldStr
        .replace(/,rate_ed/g, '')
        .replace(/,rate_m_ed/g, '')
        .replace(/,rate_m_ci_ed/g, '')
        .replace(',' + fieldMap.rate_ed.label, '')
        .replace(',' + fieldMap.rate_m_ed.label, '')
        .replace(',' + fieldMap.rate_m_ci_ed.label, '');
};
const getFieldsObjByScale = (scale) => {
    const scaleObj = getScaleObject(scale);
    return fields.find((fieldObj) => fieldObj.slug === scaleObj.slug);
};

//Use function to create a backup csv file for csnsus and zip
export const getAllFeaturesInScaleYearAsCSV = async (scale, time, token = '') => {
    return getAllFeaturesInScaleYear(scale, time, token).then((data) => {
        const scaleObj = getScaleObject(scale);
        return convertFeatureServiceDataToCSV(data, scaleObj.id, time);
    });
};
//Returns JSON for all data for a given scale and year
//Limitations of onemap require that requests must be broken up into 2k so getting all data requires paginated lookups
//Overusing this function in production can cause onemap cpu to overload and cause outages
export const getAllFeaturesInScaleYear = async (scale, time, token = '') => {
    const scaleObj = getScaleObject(scale);
    if (scaleObj.years === '*' || scaleObj.years.split(',').indexOf(time) !== -1) {
        const count = await getOneMapFeaturesCount(scale, time, token);
        const pages = Math.ceil(count / 2000);
        const promises = [];

        for (let i = 0; i < pages; i++) {
            promises.push(getOneMapFeaturesPage({ scale, time, page: i, token }));
        }
        return Promise.all(promises).then((res) => {
            return res.flat();
        });
    }
    return Promise.resolve([]);
};

//Get the total number of records in the feature service by scale and year
export const getOneMapFeaturesCount = async (scale, time, token = '') => {
    const scaleObj = getScaleObject(scale);
    const serviceURL = baseUrl(time, scaleObj.id);
    const tokenText = endpoints.useToken && token ? '&token=' + token : '';
    return axios.get(serviceURL + '?where=1%3D1&returnCountOnly=true&f=pjson' + tokenText).then((res) => {
        if (res.data.error) throw new Error('ERROR in Feature Service get count', { cause: res.data.error });
        return res.data.count;
    });
};

export const getAPageAsCSV = async (scale, time, token = '') => {
    return getOneMapFeaturesPage({ scale, time, page: 1, pgCount: 500, token }).then((data) => {
        const scaleObj = getScaleObject(scale);
        return convertFeatureServiceDataToCSV(data, scaleObj.id, time);
    });
};

//Get Feature Service records by scale and year, and page number
//page number is an index so "page 1" = 0
export const getOneMapFeaturesPage = async (args) => {
    const { scale, time, page, pgCount = 2000, transformFN = false, token = '' } = args;
    const useTransformFN = transformFN ? transformFN : (e) => e;
    const scaleObj = getScaleObject(scale);
    const serviceURL = baseUrl(time, scaleObj.id);
    const startIndex = page * pgCount;
    const rawOutFields = getFieldsObjByScale(scale).outFields;
    const outFields = filterFieldsByTime(rawOutFields, time);
    const tokenText = endpoints.useToken && token ? '&token=' + token : '';
    const byScale = convertScaleValues(scale);

    const url = serviceURL + `?where=1%3D1&outFields=${outFields}&orderByFields=${byScale}&returnGeometry=false&resultOffset=${startIndex}&resultRecordCount=${pgCount}&f=pjson${tokenText}`;

    return axios.get(url).then((res) => {
        if (res.data.error) throw new Error('ERROR in Feature Service page request', { cause: res.data.error });
        return res.data.features.map((ftr) => useTransformFN(ftr.attributes));
    });
};

export const getOneMapFeaturesPageAsCSV = async (args) => {
    const { scale, time, page, pgCount = 2000, fields = '*', transformFN = (e) => e, token = '' } = args;
    return getOneMapFeaturesPage({ scale, time, page, pgCount, transformFN, token }).then((data) => {
        return convertFeatureServiceDataToCSV(data, scale, time, fields);
    });
};
export const convertFeatureServiceDataToCSV = (data, scale, time, fields = '*') => {
    //just convert using the data we are given
    const fieldsObj = fields === '*' ? getFieldsObjByScale(scale) : addFieldIndexNames({ custom: fields })[0];
    const fieldsArr = filterFieldsByTime(fieldsObj.outFields, time).split(',');
    const indexArr = filterFieldsByTime(fieldsObj.index.join(','), time).split(',');
    var csv = '"' + indexArr.join('","') + '"\n';

    for (var i = 0; i < data.length; i++) {
        let obj = data[i];
        let line = '';
        for (var f = 0; f < fieldsArr.length; f++) {
            let field = fieldsArr[f];
            let comma = f > 0 ? ',' : '';
            let transformFN = fieldMap[field].transform;
            line += comma + '"' + String(transformFN(obj[field])).replace(/,/g, ' ') + '"';
        }
        csv += line + '\n';
    }
    return csv;
};

export const getFeatureByLonLat = async (args) => {
    const { x, y, time, scale, token = '' } = args;
    const position = {
        x: x,
        y: y,
        spatialReference: {
            wkid: 4326
        }
    };
    const scaleObj = getScaleObject(scale);
    const serviceURL = baseUrl(time, scaleObj.id);
    const geometry = encodeURIComponent(JSON.stringify(position));
    const tokenText = endpoints.useToken && token ? '&token=' + token : '';
    return axios.get(serviceURL + `?where=1%3D1&outFields=*&geometry=${geometry}&geometryType=esriGeometryPoint&inSR=4326&returnGeometry=fals&f=pjson${tokenText}`).then((res) => {
        if (res.data.error) throw new Error('ERROR in Get Feature LonLat', { cause: res.data.error });
        return res.data.features[0].attributes;
    });
};
